import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { Brain, Save, Settings, Info, Loader2 } from 'lucide-react';

interface AIModel {
    value: string;
    label: string;
    cost_input: number;
    cost_output: number;
    context: string;
    speed: string;
}

interface ProjectData {
    id: string;
    name: string;
    ai_provider: string;
    ai_model: string;
}

export const LLMAssignments: React.FC = () => {
    const [providers] = useState<{ id: string, name: string }[]>([
        { id: 'openai', name: 'OpenAI' },
        { id: 'anthropic', name: 'Anthropic' },
        { id: 'gemini', name: 'Google Gemini' }
    ]);

    const [activeProvider, setActiveProvider] = useState<string>('openai');
    const [models, setModels] = useState<Record<string, AIModel[]>>({});
    const [projects, setProjects] = useState<ProjectData[]>([]);

    // assignments: projectId -> { provider, model }
    const [assignments, setAssignments] = useState<Record<string, { provider: string, model: string }>>({});

    // For the UI: each model row needs a "currently selected project in the dropdown"
    // state: model.value -> projectId
    const [rowSelections, setRowSelections] = useState<Record<string, string>>({});

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [toast, setToast] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const projectsData = await api.getProjects();
            setProjects(projectsData);

            const initialAssignments: Record<string, { provider: string, model: string }> = {};
            projectsData.forEach((p: ProjectData) => {
                initialAssignments[p.id] = {
                    provider: p.ai_provider || 'openai',
                    model: p.ai_model || 'gpt-4o'
                };
            });
            setAssignments(initialAssignments);

            const modelsData: Record<string, AIModel[]> = {};
            for (const provider of providers) {
                const provModels = await api.getAiModels(provider.id);
                modelsData[provider.id] = provModels.map((m: any) => ({
                    value: m.value,
                    label: m.label,
                    cost_input: m.cost_input || 0,
                    cost_output: m.cost_output || 0,
                    context: m.context || 'N/A',
                    speed: m.speed || 'N/A'
                }));
            }
            setModels(modelsData);

            // Initialize row selections
            // For each model in the initial active provider, set the first project that uses it,
            // or the first available project if none use it yet.
            const initialRowSelections: Record<string, string> = {};
            if (projectsData.length > 0) {
                const oModels = modelsData['openai'] || [];
                oModels.forEach(m => {
                    const assignedProj = projectsData.find((p: ProjectData) => initialAssignments[p.id]?.model === m.value && initialAssignments[p.id]?.provider === 'openai');
                    initialRowSelections[m.value] = assignedProj ? assignedProj.id : projectsData[0].id;
                });
            }
            setRowSelections(initialRowSelections);

        } catch (error) {
            console.error("Failed to load LLM data:", error);
            setToast({ type: 'error', message: 'Error cargando datos.' });
        } finally {
            setIsLoading(false);
        }
    };

    // When provider changes, ensure rowSelections are populated for its models
    useEffect(() => {
        if (!models[activeProvider] || projects.length === 0) return;
        setRowSelections(prev => {
            const newSelections = { ...prev };
            let changed = false;
            models[activeProvider].forEach(m => {
                if (!newSelections[m.value]) {
                    // Try to find a project assigned to this model to set as default dropdown value
                    const assignedProj = projects.find(p => assignments[p.id]?.model === m.value && assignments[p.id]?.provider === activeProvider);
                    newSelections[m.value] = assignedProj ? assignedProj.id : projects[0].id;
                    changed = true;
                }
            });
            return changed ? newSelections : prev;
        });
    }, [activeProvider, models, projects, assignments]);

    const handleRowProjectChange = (modelValue: string, projectId: string) => {
        setRowSelections(prev => ({ ...prev, [modelValue]: projectId }));
    };

    const handleToggleAssign = (modelValue: string, isAssigned: boolean) => {
        const projectId = rowSelections[modelValue];
        if (!projectId) return;

        setAssignments(prev => {
            if (isAssigned) {
                // Turn ON: Assign this model to the selected project
                return {
                    ...prev,
                    [projectId]: { provider: activeProvider, model: modelValue }
                };
            } else {
                // Turn OFF: Unassign it. For simplicity, we just set it to a default or leave it. 
                // Let's fallback to openai/gpt-4o if they unassign, or just keep it as is.
                // In many "toggle" UIs, turning it off might just mean assigning the default.
                // Let's assign an empty string or default to openai/gpt-4o
                return {
                    ...prev,
                    [projectId]: { provider: 'openai', model: 'gpt-4o' }
                };
            }
        });
    };

    const handleSave = async () => {
        setIsSaving(true);
        setToast(null);
        try {
            const payload = {
                assignments: Object.entries(assignments).map(([projectId, details]) => ({
                    project_id: projectId,
                    ai_provider: details.provider,
                    ai_model: details.model
                }))
            };
            await api.bulkAssignLLMs(payload);
            setToast({ type: 'success', message: 'Configuración guardada.' });
            setTimeout(() => setToast(null), 3000);
        } catch (error) {
            console.error("Failed to save:", error);
            setToast({ type: 'error', message: 'Error al guardar.' });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
        );
    }

    const activeModels = models[activeProvider] || [];

    return (
        <div className="flex justify-center px-4 py-8 bg-[#0F172A] min-h-screen text-slate-200 font-sans">
            <div className="w-full max-w-2xl space-y-6">

                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <div className="bg-slate-800 p-3 rounded-2xl flex items-center justify-center">
                        <Brain className="w-8 h-8 text-blue-500" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white tracking-tight">Asignación de Modelos</h1>
                        <p className="text-slate-400 text-sm">Enterprise AI Gateway</p>
                    </div>
                </div>

                {toast && (
                    <div className={`p-4 rounded-xl text-sm font-medium ${toast.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                        {toast.message}
                    </div>
                )}

                {/* Provider Select */}
                <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">
                        Proveedor de IA
                    </label>
                    <div className="relative">
                        <select
                            value={activeProvider}
                            onChange={(e) => setActiveProvider(e.target.value)}
                            className="w-full bg-[#1E293B] border border-slate-700/50 text-white rounded-xl px-4 py-3.5 appearance-none focus:ring-2 focus:ring-blue-500 focus:outline-none font-medium text-sm transition-shadow shadow-sm"
                        >
                            {providers.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>
                </div>

                <div className="w-full h-px bg-slate-800 my-6"></div>

                {/* Models Section */}
                <div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Modelos Disponibles</h2>
                        <button className="text-blue-500 flex items-center gap-1.5 text-sm font-medium hover:text-blue-400 transition-colors">
                            <Settings className="w-4 h-4" />
                            Configurar todos
                        </button>
                    </div>

                    <div className="bg-[#1E293B] border border-slate-700/50 rounded-2xl overflow-hidden shadow-xl">
                        {/* Table Header */}
                        <div className="grid grid-cols-[1fr_140px_auto] gap-4 px-5 py-3 border-b border-slate-700/50 bg-[#1e293b]/50">
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Modelo & Precio</div>
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Proyecto</div>
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider text-center">Asig.</div>
                        </div>

                        {/* Model Rows */}
                        <div className="divide-y divide-slate-700/50">
                            {activeModels.map(model => {
                                // Find which project is selected in the dropdown for this row
                                const currentProjId = rowSelections[model.value] || projects[0]?.id || '';
                                // Check if the selected project actually has this model assigned in our state
                                const isAssigned = assignments[currentProjId]?.model === model.value && assignments[currentProjId]?.provider === activeProvider;

                                return (
                                    <div key={model.value} className="grid grid-cols-[1fr_140px_auto] items-center gap-4 p-5 hover:bg-slate-800/30 transition-colors">

                                        {/* Col 1: Model Info */}
                                        <div>
                                            <div className="font-bold text-slate-200 text-base mb-0.5">{model.label}</div>
                                            <div className="text-[13px] font-medium mb-0.5">
                                                <span className="text-emerald-400">${model.cost_input}</span>
                                                <span className="text-emerald-400"> / ${model.cost_output}</span>
                                                <span className="text-slate-500"> (1M Tokens)</span>
                                            </div>
                                            <div className="text-[13px] text-slate-500">
                                                Contexto: {model.context}
                                            </div>
                                        </div>

                                        {/* Col 2: Project Select */}
                                        <div className="relative">
                                            <select
                                                value={currentProjId}
                                                onChange={(e) => handleRowProjectChange(model.value, e.target.value)}
                                                className="w-full bg-[#0F172A] border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-sm appearance-none focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 truncate pr-8"
                                            >
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                                {projects.length === 0 && <option value="">Ninguno</option>}
                                            </select>
                                            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                            </div>
                                        </div>

                                        {/* Col 3: Toggle */}
                                        <div className="flex justify-center pl-2">
                                            <button
                                                onClick={() => handleToggleAssign(model.value, !isAssigned)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900 ${isAssigned ? 'bg-blue-500' : 'bg-slate-600'
                                                    }`}
                                            >
                                                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${isAssigned ? 'translate-x-5' : 'translate-x-1'
                                                    }`} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}

                            {activeModels.length === 0 && (
                                <div className="p-8 text-center text-slate-500 text-sm">
                                    No hay modelos disponibles para este proveedor.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Info Box */}
                <div className="bg-[#1E293B] border border-slate-700/50 rounded-2xl p-5 flex gap-4 shadow-sm items-start mt-6">
                    <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-slate-300 leading-relaxed">
                        Los precios mostrados son estimaciones aproximadas basadas en 1M de tokens de entrada y salida combinados.
                    </p>
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-6">
                    <button
                        className="flex-1 py-3.5 px-4 bg-[#1E293B] hover:bg-slate-700 border border-slate-700/50 text-slate-300 rounded-xl font-medium transition-colors text-sm"
                        onClick={() => window.history.back()}
                    >
                        Cancelar
                    </button>
                    <button
                        className="flex-1 py-3.5 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Guardar Cambios
                    </button>
                </div>

            </div>
        </div>
    );
};

